// Pure date helpers for the operational calendar + Gantt views. Everything is
// local-time on purpose: the app runs in the user's timezone (RO = UTC+2/+3)
// and `toISOString()` would shift a local midnight into the previous day.

/** Local-time `YYYY-MM-DD` key for a Date (never via toISOString). */
export function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** `YYYY-MM-DD` bucket key from an API ISO string (dates are stored as UTC midnights). */
export function dateKeyOf(iso: string): string {
  return iso.slice(0, 10);
}

/**
 * Local-midnight Date from an API ISO string / `YYYY-MM-DD` key. Parsing only
 * the date part avoids the UTC→local shift `new Date(iso)` would introduce.
 */
export function fromDateKey(isoOrKey: string): Date {
  const [y, m, d] = isoOrKey.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** A day plus N days (native Date normalizes overflow, incl. DST). */
export function addDays(d: Date, days: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
}

/** First day of the month shifted by N months (day clamped to 1). */
export function addMonths(d: Date, months: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + months, 1);
}

/** The Monday of the week containing `d` (local midnight). */
export function startOfWeekMonday(d: Date): Date {
  const weekday = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
  return addDays(d, -weekday);
}

export interface MonthGridDay {
  date: Date;
  key: string;
  inMonth: boolean;
}

/**
 * 6×7 month grid starting on Monday, padded with adjacent-month days so the
 * calendar always shows full weeks (42 cells).
 */
export function monthGrid(year: number, month: number): MonthGridDay[][] {
  const first = new Date(year, month, 1);
  const firstWeekday = (first.getDay() + 6) % 7; // Mon=0
  const gridStart = addDays(first, -firstWeekday);
  const weeks: MonthGridDay[][] = [];
  for (let w = 0; w < 6; w++) {
    const week: MonthGridDay[] = [];
    for (let i = 0; i < 7; i++) {
      const date = addDays(gridStart, w * 7 + i);
      week.push({ date, key: toDateKey(date), inMonth: date.getMonth() === month });
    }
    weeks.push(week);
  }
  return weeks;
}

/**
 * The 7 days (Monday-first) of the week containing `d`. Reuses MonthGridDay so
 * the week view can share the task-chip cell shape with the month grid; every
 * day belongs to the week, so `inMonth` is always true here.
 */
export function weekGrid(d: Date): MonthGridDay[] {
  const start = startOfWeekMonday(d);
  return Array.from({ length: 7 }, (_, i) => {
    const date = addDays(start, i);
    return { date, key: toDateKey(date), inMonth: true };
  });
}

/** Whole days between two local dates (midnight-to-midnight; round absorbs DST). */
export function daysBetween(a: Date, b: Date): number {
  const aMid = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const bMid = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((bMid.getTime() - aMid.getTime()) / 86_400_000);
}

export interface BarGeometry {
  leftPct: number;
  widthPct: number;
  clippedStart: boolean;
  clippedEnd: boolean;
}

/**
 * Position a Gantt bar inside a [horizonStart, horizonEnd) window as left/width
 * percentages. `end === null` means open-ended (runs to the horizon edge).
 * Returns null when the bar does not intersect the horizon.
 */
export function barGeometry(
  horizonStart: Date,
  horizonEnd: Date,
  start: Date,
  end: Date | null,
): BarGeometry | null {
  const totalDays = daysBetween(horizonStart, horizonEnd);
  if (totalDays <= 0) return null;

  let startDay = daysBetween(horizonStart, start);
  // End is inclusive (an allocation ending on day X still covers day X).
  let endDay = end === null ? totalDays : daysBetween(horizonStart, end) + 1;

  if (endDay <= 0 || startDay >= totalDays) return null;

  const clippedStart = startDay < 0;
  const clippedEnd = endDay > totalDays;
  if (clippedStart) startDay = 0;
  if (clippedEnd) endDay = totalDays;
  if (endDay <= startDay) endDay = startDay + 1;

  return {
    leftPct: (startDay / totalDays) * 100,
    widthPct: ((endDay - startDay) / totalDays) * 100,
    clippedStart,
    clippedEnd,
  };
}

/** "iulie 2026" / "July 2026" — month label via Intl, no dictionary keys. */
export function monthLabel(locale: string, year: number, month: number): string {
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(
    new Date(year, month, 1),
  );
}

/** Short weekday labels Monday-first ("lun", "mar", …). */
export function weekdayLabels(locale: string): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  // 2024-01-01 is a Monday.
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 1 + i)));
}

/** "12 ian" / "Jan 12" — compact day+month label for Gantt week headers. */
export function shortDayLabel(locale: string, d: Date): string {
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(d);
}
